import type { LapRef } from '@tla/types';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { useCursorStore } from '../../store/cursorStore';
import { lapColour, lapKey } from '../../store/colours';
import { formatLapTime } from '../../util/format';

/** Toolbar above the graph stack: mode toggle, zoom reset, cursor readout, chips. */
export function GraphToolbar(): React.ReactElement {
  const graphMode = useWorkspaceStore((s) => s.graphMode);
  const setGraphMode = useWorkspaceStore((s) => s.setGraphMode);
  const graphGrid = useWorkspaceStore((s) => s.graphGrid);
  const setGraphGrid = useWorkspaceStore((s) => s.setGraphGrid);
  const zoom = useWorkspaceStore((s) => s.zoom);
  const setZoom = useWorkspaceStore((s) => s.setZoom);
  const referenceLap = useWorkspaceStore((s) => s.referenceLap);
  const comparisonLaps = useWorkspaceStore((s) => s.comparisonLaps);
  const removeLap = useWorkspaceStore((s) => s.removeLap);
  const clearLaps = useWorkspaceStore((s) => s.clearLaps);
  const hiddenLaps = useWorkspaceStore((s) => s.hiddenLaps);
  const toggleLapHidden = useWorkspaceStore((s) => s.toggleLapHidden);
  const summaries = useWorkspaceStore((s) => s.summaries);
  const sessions = useWorkspaceStore((s) => s.sessions);
  const drivers = useWorkspaceStore((s) => s.drivers);

  const active = useCursorStore((s) => s.active);
  const locked = useCursorStore((s) => s.locked);
  const clearLock = useCursorStore((s) => s.clearLock);

  const laps: LapRef[] = referenceLap ? [referenceLap, ...comparisonLaps] : [];

  /** Descriptor parts for a lap chip. */
  function lapInfo(ref: LapRef): { driver: string; stint: string | null; time: string } {
    const session = sessions[ref.sessionId];
    const driver = session ? (drivers[session.driverId]?.name ?? 'Driver') : 'Driver';
    const stint = session ? session.stint : null;
    const lapTime = summaries[ref.sessionId]?.laps.find((l) => l.number === ref.lapNumber)?.lapTime;
    return { driver, stint, time: formatLapTime(lapTime) };
  }

  return (
    <div className="toolbar">
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          className={graphMode === 'tiled' ? 'primary' : ''}
          onClick={() => setGraphMode('tiled')}
        >
          Tiled
        </button>
        <button
          className={graphMode === 'overlapped' ? 'primary' : ''}
          onClick={() => setGraphMode('overlapped')}
        >
          Overlapped
        </button>
      </div>

      {graphMode === 'tiled' && (
        <button
          className={`icon-btn grid-toggle ${graphGrid ? 'primary' : ''}`}
          onClick={() => setGraphGrid(!graphGrid)}
          title={graphGrid ? 'Two-up grid view (click for single column)' : 'Single column view (click for two-up grid)'}
          aria-label={graphGrid ? 'Grid view' : 'List view'}
        >
          {graphGrid ? (
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <rect x="1" y="1" width="6" height="6" rx="1" fill="currentColor" />
              <rect x="9" y="1" width="6" height="6" rx="1" fill="currentColor" />
              <rect x="1" y="9" width="6" height="6" rx="1" fill="currentColor" />
              <rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <rect x="1" y="2" width="14" height="3" rx="1" fill="currentColor" />
              <rect x="1" y="6.5" width="14" height="3" rx="1" fill="currentColor" />
              <rect x="1" y="11" width="14" height="3" rx="1" fill="currentColor" />
            </svg>
          )}
        </button>
      )}

      <button onClick={() => setZoom(null)} disabled={!zoom}>
        Reset zoom
      </button>

      <div className="readout small muted">
        {active != null ? `${active.toFixed(0)} m` : '—'}
        {locked != null && (
          <>
            {' · lock '}
            {locked.toFixed(0)} m
            {active != null && ` · Δ${(active - locked).toFixed(0)} m`}
            <button className="small" style={{ marginLeft: 6 }} onClick={clearLock}>
              unlock
            </button>
          </>
        )}
      </div>

      <div className="spacer" style={{ flex: 1 }} />

      <div className="chip-row" style={{ padding: 0 }}>
        {laps.map((ref, i) => {
          const info = lapInfo(ref);
          const hidden = hiddenLaps.includes(lapKey(ref));
          const full = `${info.driver}${info.stint ? ` · Stint ${info.stint}` : ''} · Lap ${ref.lapNumber} · ${info.time}${hidden ? ' (hidden — click to show)' : ' (click to hide)'}`;
          return (
            <span
              key={lapKey(ref)}
              className={`chip lap-chip${hidden ? ' hidden' : ''}`}
              title={full}
              role="button"
              tabIndex={0}
              onClick={() => toggleLapHidden(ref)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleLapHidden(ref);
                }
              }}
            >
              <span className="swatch" style={{ background: hidden ? 'transparent' : lapColour(i), borderColor: lapColour(i) }} />
              <span className="lap-chip-main">
                <span className="lap-chip-driver">{info.driver}</span>
                <span className="lap-chip-sub muted">
                  {info.stint ? `S${info.stint} · ` : ''}L{ref.lapNumber} · {info.time}
                </span>
              </span>
              <button
                title="Remove lap"
                onClick={(e) => {
                  e.stopPropagation();
                  removeLap(ref);
                }}
              >
                ✕
              </button>
            </span>
          );
        })}
        {laps.length > 0 && (
          <button className="small" onClick={clearLaps}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
